import { assertNever } from "@@/shared/assert-never";
import { asDate } from "@@/shared/date-time/as-date";
import { DAY } from "@@/shared/date-time/time-constants";
import { timeRangeUtils, type TimeRange } from "@@/shared/date-time/time-range";
import { createTimeSpot } from "@@/shared/date-time/time-spot";
import type { Order } from "@@/shared/orders/order";
import type { ProductId } from "../../products/product-id";
import {
    isOrderItemAccommodationRateItem,
    isOrderItemBookableQuantity,
    isOrderItemOptional,
} from "./is-order-item";
import type {
    OrderItem,
    OrderItemAccommodationRateItem,
    OrderItemBookableQuantity,
    OrderItemOptional,
} from "./order-item";
import type { OrderItemWithProduct_V2 } from "./order-item-v2";

const getChildren = (orderItem: OrderItem, orderItems: OrderItem[]) => {
    return orderItems.filter(oi => oi.parentId === orderItem._id);
};

/**
 * Retrieves all OrderItemOptional items that are children of a specified OrderItem.
 *
 * @param orderItem - The parent OrderItem whose children to retrieve
 * @param orderItems - Array of all OrderItem objects to filter from
 * @returns An array of OrderItemOptional items that have the specified orderItem as parent
 */
const getChildOptionals = (
    orderItem: OrderItem,
    orderItems: OrderItem[],
): OrderItemOptional[] => {
    return orderItems
        .filter(isOrderItemOptional)
        .filter(oi => oi.parentId === orderItem._id);
};

/**
 * Retrieves all bookable quantity items that are children of the specified order item.
 *
 * @param orderItem - The parent order item to find children for
 * @param orderItems - Array of all order items to search through
 * @returns An array of order item bookable quantities that have the specified order item as their parent
 */
const getChildQuantities = (
    orderItem: OrderItem,
    orderItems: OrderItem[],
): OrderItemBookableQuantity[] => {
    return orderItems
        .filter(isOrderItemBookableQuantity)
        .filter(oi => oi.parentId === orderItem._id);
};

/**
 * Retrieves all child accommodation rate items for a given order item.
 *
 * @param orderItem - The parent order item to find children for
 * @param orderItems - The array of all order items to search through
 * @returns An array of OrderItemAccommodationRateItem that are children of the provided orderItem
 */
const getChildAccommodationRates = (
    orderItem: OrderItem,
    orderItems: OrderItem[],
): OrderItemAccommodationRateItem[] => {
    return orderItems
        .filter(isOrderItemAccommodationRateItem)
        .filter(oi => oi.parentId === orderItem._id);
};

/**
 * Extracts and returns an array of ProductIds from an array of OrderItems.
 *
 * This function processes each OrderItem in the provided array to extract its
 * associated ProductId. Any OrderItems that don't have a ProductId (i.e., `getProductId`
 * returns a falsy value) are filtered out from the result.
 *
 * @param {OrderItem[]} orderItems - An array of OrderItems to extract ProductIds from
 * @returns {ProductId[]} An array of ProductIds extracted from the OrderItems
 */
const getProductIds = (orderItems: OrderItem[]): ProductId[] => {
    return orderItems.flatMap(oi => {
        const prodId = getProductId(oi);
        return prodId ? prodId : [];
    });
};

const getProductId = (orderItem: OrderItem) => {
    switch (orderItem.acquireType) {
        case "BOOKABLE":
        case "BUYABLE":
        case "VOUCHER":
        case "GIFTCARD":
        case "ACCOMMODATION":
            return orderItem.productId;
        case "RESOURCE_REFERENCE":
        case "OPTIONAL":
        case "GIFTCARD_USED":
        case "DISCOUNT":
        case "GENERIC_DISCOUNT":
        case "ACCOMMODATION_RATE_ITEM":
        case "GENERIC":
        case "QUOTE_HEAD":
            return undefined;
        default:
            assertNever(orderItem);
    }
};

type OrderItemWithProduct = OrderItemWithProduct_V2;

const isOrderItemWithProduct = (oi: OrderItem): oi is OrderItemWithProduct => {
    return (
        oi.acquireType === "BUYABLE" ||
        oi.acquireType === "BOOKABLE" ||
        oi.acquireType === "VOUCHER" ||
        oi.acquireType === "GIFTCARD" ||
        oi.acquireType === "ACCOMMODATION"
    );
};

/**
 * Determines if the given `OrderItem` has an associated product and optionally checks
 * if it matches a specific product ID.
 *
 * @param oi - The `OrderItem` to check.
 * @param productId - (Optional) The specific product ID to match against.
 * @returns A type guard indicating whether the `OrderItem` has an associated product.
 *          If `productId` is provided, it also checks if the product ID matches.
 */
const orderItemHasProduct = (
    oi: OrderItem,
    productId?: ProductId,
): oi is OrderItemWithProduct => {
    const hasProduct = isOrderItemWithProduct(oi);
    if (!hasProduct) return false;
    if (!productId) return hasProduct;

    const hasSpecificProduct = oi.productId === productId;
    return hasSpecificProduct;
};
/**
 * Type to represent order items that have a time property
 */
type OrderItemWithBookingTime = Extract<OrderItem, { time: TimeRange }>;

/**
 * Type guard to check if an order item has a time property
 * @param orderItem The order item to check
 * @returns True if the order item has a time property, false otherwise
 */
const orderItemHasTime = (
    orderItem: OrderItem,
): orderItem is OrderItemWithBookingTime => {
    return (
        orderItem.acquireType === "BOOKABLE" ||
        orderItem.acquireType === "ACCOMMODATION" ||
        orderItem.acquireType === "QUOTE_HEAD"
    );
};

const getTimeFromOrderItem = (order: Order) => {
    const allTime = order.orderItems
        .filter(orderItemHasTime)
        .map(oi => oi.time);

    if (allTime.length === 0) return createTimeSpot(new Date());

    const time = timeRangeUtils.mergeTimeRanges(allTime);

    const isLongerThanADay = time.duration > DAY / 1000;

    if (isLongerThanADay) return time;

    return createTimeSpot(asDate(time.start));
};

export {
    getChildAccommodationRates,
    getChildOptionals,
    getChildQuantities,
    getChildren,
    getProductId,
    getProductIds,
    getTimeFromOrderItem,
    isOrderItemWithProduct,
    orderItemHasProduct,
    orderItemHasTime,
};
export type { OrderItemWithBookingTime, OrderItemWithProduct };
